/**
 * Loading a video's subtitles and annotations for the student page, fast.
 *
 * A Firestore read takes ~1.5–2s from a learner's browser, and the Firestore
 * SDK can take far longer on iOS Safari while it sets up its connection. So
 * the student page doesn't wait on either:
 *
 *   1. snapshot — /data/videos/{videoId}.json, published to hoja's Hosting
 *      (CDN) on every deploy and hourly (scripts/export-learner-data.mjs).
 *      Shown as soon as it arrives.
 *   2. live — the same data straight from Firestore over its REST API (one
 *      plain HTTPS request per collection, no SDK), so a teacher's latest
 *      edits replace the snapshot moments later.
 *
 * Both return { subtitles, annotations } or throw. Subtitles and annotations
 * are public-read (firestore.rules), so no sign-in is involved.
 */

import { decodeDocument } from './firestoreRest';

export const SNAPSHOT_VERSION = 1;
const PROJECT = 'hoja-copilot';
const RUN_QUERY = `https://firestore.googleapis.com/v1/projects/${PROJECT}/databases/(default)/documents:runQuery`;

export async function fetchSnapshot(videoId, fetchFn = fetch) {
  const res = await fetchFn(`/data/videos/${encodeURIComponent(videoId)}.json`);
  if (!res.ok) throw new Error(`No published data for ${videoId}`);
  const file = await res.json(); // a missing file is served as index.html: this throws
  if (file.version !== SNAPSHOT_VERSION || file.videoId !== videoId) throw new Error('Unexpected snapshot');
  return { subtitles: file.subtitles || [], annotations: file.annotations || [] };
}

// Every document in `collectionId` whose videoId matches.
export async function queryByVideo(collectionId, videoId, fetchFn = fetch) {
  const res = await fetchFn(RUN_QUERY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      structuredQuery: {
        from: [{ collectionId }],
        where: { fieldFilter: { field: { fieldPath: 'videoId' }, op: 'EQUAL', value: { stringValue: videoId } } },
      },
    }),
  });
  if (!res.ok) throw new Error(`Firestore ${res.status}`);
  const rows = await res.json();
  return rows.filter((r) => r.document).map((r) => decodeDocument(r.document));
}

export async function fetchLive(videoId, fetchFn = fetch) {
  const [subtitles, annotations] = await Promise.all([
    queryByVideo('subtitles', videoId, fetchFn),
    queryByVideo('annotations', videoId, fetchFn),
  ]);
  return { subtitles, annotations };
}
